import type { AppProps } from 'next/app'
import type PageAppOptions from 'lib/pageAppOptions'
import Head from 'next/head'
// eslint-disable-next-line import/no-unassigned-import
import 'styles/tailwind.css'
// eslint-disable-next-line import/no-unassigned-import
import 'styles/global.css'
import { ConfirmDialogProvider } from 'components/ConfirmDialog'
import { ToastProvider } from 'components/Toast'
import { BlockUiProvider } from 'components/BlockUi'
import Modal from 'react-modal'
import { PromptDialogProvider } from 'components/PromptDialog'
import { AnalyticsScript, applyConsent, logPageView } from 'utils/analytics'
import {
  isTrackingOptedOut,
  notifyTrackingOptOut
} from 'utils/privacyPreferences'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import useDeepCompareEffect from 'hooks/useDeepCompareEffect'
import isEqual from 'lodash/isEqual'
// eslint-disable-next-line import/no-unassigned-import
import 'what-input'
import { InfoDialogProvider } from 'components/InfoDialog'
import apiUrl from 'utils/apiUrl'
import queueTrackingRequest from 'utils/queueTrackingRequest'
import getLinkedInIdFromCookie from 'utils/getLinkedInIdFromCookie'
import { inter, roboto, figtree, libreFranklin } from 'lib/fonts'

const CLICK_ID_PARAMS = new Set([
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS app-to-web)
  'wbraid', // Google Ads (iOS web-to-app)
  'gclsrc', // Google Ads click source (e.g. aw.ds, ds)
  'gad_source', // Google Ads source/network
  'gad_campaignid', // Google Ads campaign ID
  'fbclid', // Meta
  'msclkid', // Microsoft / Bing
  'li_fat_id' // LinkedIn Ads
])

if (typeof window !== 'undefined') {
  Modal.setAppElement('#__next')
}

type AppPropsWithOptions = AppProps & {
  Component: AppProps['Component'] & PageAppOptions
}

export default function App({ Component, pageProps }: AppPropsWithOptions) {
  const router = useRouter()
  const lastReportedTracking = useRef<Record<string, string> | null>(null)
  const { disableAnalytics } = Component

  // https://github.com/vercel/next.js/blob/canary/examples/with-react-ga/pages/_app.js
  useEffect(() => {
    // `routeChangeComplete` won't run for the first page load unless the query string is
    // hydrated later on, so here we log a page view if this is the first render and
    // there's no query string
    if (!disableAnalytics && !router.asPath.includes('?')) {
      logPageView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (disableAnalytics) return undefined

    // Listen for page changes after a navigation or when the query changes
    router.events.on('routeChangeComplete', logPageView)
    return () => {
      router.events.off('routeChangeComplete', logPageView)
    }
  }, [router.events, disableAnalytics])

  useEffect(() => {
    applyConsent()
  }, [])

  useDeepCompareEffect(() => {
    // Statically optimized pages hydrate with an empty query, which would report
    // the landing page without its click ids
    if (!router.isReady || disableAnalytics) return

    const optOutSource = isTrackingOptedOut()
    if (optOutSource) {
      notifyTrackingOptOut(optOutSource)
      return
    }

    const trackingParams: Record<string, string> = {}

    Object.keys(router.query).forEach((key) => {
      if (key.startsWith('utm_') || CLICK_ID_PARAMS.has(key)) {
        const value = router.query[key]
        trackingParams[key] = Array.isArray(value)
          ? value[0]
          : (value as string)
      }
    })

    trackingParams.li_fat_id ||= getLinkedInIdFromCookie() || ''
    if (!trackingParams.li_fat_id) delete trackingParams.li_fat_id

    if (document.referrer) {
      let referrerIsSameOrigin = false
      try {
        referrerIsSameOrigin =
          new URL(document.referrer).origin === window.location.origin
      } catch {
        // malformed referrer — treat as external
      }
      if (!referrerIsSameOrigin) {
        trackingParams.referrer = document.referrer
      }
    }

    trackingParams.navigation = router.asPath.split(/[?#]/)[0]

    if (Object.keys(trackingParams).length === 0) {
      return
    }

    // Avoid re-reporting when the URL's query params change but the values we
    // actually report stay the same.
    if (isEqual(trackingParams, lastReportedTracking.current)) {
      return
    }
    lastReportedTracking.current = trackingParams

    queueTrackingRequest(`${apiUrl()}/track-referrer.json`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        web_tracking: trackingParams
      })
    })
  }, [router.isReady, router.asPath, router.query, disableAnalytics])

  return (
    <main
      className={`${inter.variable} ${roboto.variable} ${figtree.variable} ${libreFranklin.variable}`}
    >
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style global jsx>
        {`
          html {
            --font-roboto: ${roboto.style.fontFamily};
            --font-libre-franklin: ${libreFranklin.style.fontFamily};
          }
        `}
      </style>
      <Head>
        {process.env.NEXT_PUBLIC_NO_INDEX ? (
          <meta content="noindex,nofollow" name="robots" />
        ) : null}
        <meta content="max-image-preview:large" name="robots" />
        <link
          href="/apple-touch-icon.png"
          rel="apple-touch-icon"
          sizes="180x180"
        />
        <link
          href="/favicon-32x32.png"
          rel="icon"
          sizes="32x32"
          type="image/png"
        />
        <link
          href="/favicon-16x16.png"
          rel="icon"
          sizes="16x16"
          type="image/png"
        />
        <meta content="initial-scale=1.0, width=device-width" name="viewport" />
        <meta content="#FF9800" name="theme-color" />
        <link crossOrigin="use-credentials" href={apiUrl()} rel="preconnect" />
        <title key="title">
          Best Food Trucks (BFT): Find & Book Food Trucks for Catering, Events,
          Weddings, and Office Lunches
        </title>
        <meta
          key="description"
          content="Find & Book Food Trucks for Catering, Events, Weddings, and Office Lunches"
          name="description"
        />
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `window.MSInputMethodContext && document.documentMode && document.write('<script src="https://cdn.jsdelivr.net/gh/nuxodin/ie11CustomProperties@4.1.0/ie11CustomProperties.min.js"><\\/script>');`
          }}
        />
      </Head>
      {!disableAnalytics && <AnalyticsScript />}
      <ToastProvider>
        <InfoDialogProvider>
          <ConfirmDialogProvider>
            <PromptDialogProvider>
              <BlockUiProvider>
                <Component {...pageProps} />
              </BlockUiProvider>
            </PromptDialogProvider>
          </ConfirmDialogProvider>
        </InfoDialogProvider>
      </ToastProvider>
    </main>
  )
}
